// frontend/src/pages/Guide.js
import React, { useMemo, useState, useEffect, useRef, useCallback, Suspense } from 'react';
import useChannelsStore from '../store/channels';
import useLogosStore from '../store/logos';
import useVideoStore from '../store/useVideoStore';
import useSettingsStore from '../store/settings';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  LoadingOverlay,
  Paper,
  Select,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { Clock, Search, X } from 'lucide-react';
import './guide.css';
import useEPGsStore from '../store/epgs';
import { useElementSize } from '@mantine/hooks';
import { VariableSizeList } from 'react-window';
import {
  buildChannelIdMap,
  calcProgressPct,
  calculateHourTimeline,
  calculateNowPosition,
  calculateScrollPosition,
  calculateScrollPositionByTimeClick,
  CHANNEL_WIDTH,
  computeRowHeights,
  createRecording,
  createSeriesRule,
  evaluateSeriesRulesByTvgId,
  fetchPrograms,
  fetchRules,
  filterGuideChannels,
  formatSeasonEpisode,
  formatTime,
  getGroupOptions,
  getProfileOptions,
  getRuleByProgram,
  HOUR_WIDTH,
  mapProgramsByChannel,
  mapRecordingsByProgramId,
  matchChannelByTvgId,
  MINUTE_BLOCK_WIDTH,
  MINUTE_INCREMENT,
  PROGRAM_GAP_PX,
  PROGRAM_HEIGHT,
  PX_PER_MS,
  sortChannels,
} from '../utils/guideUtils';
import {
  appendWindowParams,
  CHUNK_MS,
  cullProgramsToKeepWindow,
  getInitialWindow,
  KEEP_MS,
  mergeProgramsById,
  nextChunkBackward,
  nextChunkForward,
  ORIGIN_FORWARD_MS,
  ORIGIN_LOOKBACK_MS,
  shouldPrefetchBackward,
  shouldPrefetchForward,
  viewportTimeRange,
  WARMUP_BACK_MS,
  WARMUP_FORWARD_MS,
} from '../utils/guideWindow';
import {
  horizontalWheelAmount,
  normalizeWheelDelta,
  wheelGestureAxis,
} from '../utils/guideWheel.js';
import API from '../api';
import { getShowVideoUrl } from '../utils/cards/RecordingCardUtils.js';
import {
  convertToMs,
  format,
  getNow,
  initializeTime,
  startOfHour,
  useDateTimeFormat,
} from '../utils/dateTimeUtils.js';
import GuideRow from '../components/GuideRow.jsx';
import ProviderGroupMenu from '../components/ProviderGroupMenu.jsx';
import HourTimeline from '../components/HourTimeline';
const ProgramRecordingModal = React.lazy(
  () => import('../components/forms/ProgramRecordingModal')
);
const SeriesRecordingModal = React.lazy(
  () => import('../components/forms/SeriesRecordingModal')
);
const ProgramDetailModal = React.lazy(
  () => import('../components/ProgramDetailModal')
);
import { showNotification } from '../utils/notificationUtils.js';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import useAuthStore from '../store/auth';
import { canManageDvr } from '../utils/dvrAccess';

export default function TVChannelGuide({ startDate, endDate }) {
  const [isChannelsLoading, setIsChannelsLoading] = useState(false);
  const [allowAllGroups, setAllowAllGroups] = useState(true);
  const MAX_ALL_CHANNELS = 99999;

  const recordings = useChannelsStore((s) => s.recordings);
  const channelGroups = useChannelsStore((s) => s.channelGroups);
  const profiles = useChannelsStore((s) => s.profiles);
  const [isProgramsLoading, setIsProgramsLoading] = useState(true);
  const authUser = useAuthStore((s) => s.user);
  const canManage = canManageDvr(authUser);

  const enableLogoRendering = useLogosStore((s) => s.enableLogoRendering);
  useEffect(() => {
    enableLogoRendering();
  }, [enableLogoRendering]);

  const tvgsById = useEPGsStore((s) => s.tvgsById);
  const epgs = useEPGsStore((s) => s.epgs);

  const [programs, setPrograms] = useState([]);
  const [guideChannels, setGuideChannels] = useState([]);
  // Contiguous program window loaded from the grid API (ms since epoch).
  const [loadedRange, setLoadedRange] = useState(null);
  const loadedRangeRef = useRef(null);
  const programProfileRef = useRef(null);
  const extendInflightRef = useRef({ forward: false, backward: false });
  const windowEpochRef = useRef(0);
  const [now, setNow] = useState(getNow());
  const [selectedProgram, setSelectedProgram] = useState(null);
  // DISPATCHARR_GUIDE_EXACT_LIVE_STATE_R8_8_24
  const [selectedProgramIsLive, setSelectedProgramIsLive] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [recordingForProgram, setRecordingForProgram] = useState(null);
  const [recordChoiceOpen, setRecordChoiceOpen] = useState(false);
  const [recordChoiceProgram, setRecordChoiceProgram] = useState(null);
  const [recordChoiceChannel, setRecordChoiceChannel] = useState(null);
  const [existingRuleMode, setExistingRuleMode] = useState(null);
  const [existingRule, setExistingRule] = useState(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const [initialScrollComplete, setInitialScrollComplete] = useState(false);

  // New filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('all');
  const [selectedProfileId, setSelectedProfileId] = useState('all');

  const env_mode = useSettingsStore((s) => s.environment.env_mode);

  const guideRef = useRef(null);
  const timelineRef = useRef(null); // New ref for timeline scrolling
  const listRef = useRef(null);
  const tvGuideRef = useRef(null); // Ref for the main tv-guide wrapper
  const isSyncingScroll = useRef(false);
  const guideScrollLeftRef = useRef(0);
  const nowLineRef = useRef(null);
  const [settledScrollLeft, setSettledScrollLeft] = useState(0);
  const scrollDebounceRef = useRef(null);
  // DISPATCHARR_GUIDE_SCROLL_BACK_R74: growing the window moves the whole
  // guide sideways, so it has to wait for the scroll to be still.
  const lastScrollAtRef = useRef(0);
  const touchActiveRef = useRef(false);
  const pendingWindowApplyRef = useRef([]);
  const pendingWindowTimerRef = useRef(null);
  // DISPATCHARR_GUIDE_JUMP_TO_NOW_R75: set while the clock button is waiting
  // for a reloaded window to render.
  const pendingNowJumpRef = useRef(false);
  const {
    ref: guideContainerRef,
    width: guideWidth,
    height: guideHeight,
  } = useElementSize();

  // Decide if 'All Channel Groups' should be enabled (based on total channel count)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams();
        const ids = await API.getAllChannelIds(params);
        if (cancelled) {
          return;
        }

        const total = Array.isArray(ids)
          ? ids.length
          : (ids?.length ?? ids?.count ?? 0);
        setAllowAllGroups(total <= MAX_ALL_CHANNELS);
      } catch (e) {
        // If we cannot determine, keep current default (true)
        console.error('Failed to get total channel IDs', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // If 'All' is not allowed, default to the first available group
  useEffect(() => {
    if (!allowAllGroups && selectedGroupId === 'all') {
      const firstGroup = Object.values(channelGroups).find(
        (g) => g?.hasChannels
      );
      if (firstGroup) {
        setSelectedGroupId(String(firstGroup.id));
      }
    }
  }, [allowAllGroups, channelGroups, selectedGroupId]);

  // Group changes are client-side when All is allowed; only refetch when the
  // forced single-group API path needs a different group.
  const groupRefetchKey = allowAllGroups ? 'all' : selectedGroupId;

  // Fetch channels on demand based on filters
  useEffect(() => {
    let cancelled = false;
    const loadGuideData = async () => {
      try {
        setIsChannelsLoading(true);
        const params = new URLSearchParams();
        // When All groups is allowed, fetch the whole profile-scoped channel
        // set and apply the group dropdown filter in the browser. Only ask the
        // API for a specific group when All is disabled (channel count above
        // MAX_ALL_CHANNELS).
        if (!allowAllGroups) {
          const group =
            selectedGroupId !== 'all'
              ? channelGroups[Number(selectedGroupId)]
              : Object.values(channelGroups).find((g) => g?.hasChannels);
          if (group?.name) params.set('channel_group', group.name);
        }

        // Profile filter (shared with the programs request so scopes match).
        const profileParam =
          selectedProfileId && selectedProfileId !== 'all'
            ? String(selectedProfileId)
            : null;
        if (profileParam) {
          params.set('channel_profile_id', profileParam);
        }

        // Search filter
        if (searchQuery && searchQuery.trim() !== '') {
          params.set('search', searchQuery.trim());
        }

        // Fetch channels and programs in parallel.
        const programParams = new URLSearchParams();
        if (profileParam) {
          programParams.set('channel_profile_id', profileParam);
        }
        const initialWindow = getInitialWindow(convertToMs(getNow()));
        appendWindowParams(
          programParams,
          initialWindow.startMs,
          initialWindow.endMs
        );
        programProfileRef.current = profileParam;
        extendInflightRef.current = { forward: false, backward: false };
        const loadEpoch = ++windowEpochRef.current;

        const [channels, programData] = await Promise.all([
          API.getChannelsSummary(params),
          fetchPrograms(programParams),
        ]);

        if (cancelled || windowEpochRef.current !== loadEpoch) return;

        setGuideChannels(sortChannels(channels || []));
        setPrograms(programData);
        loadedRangeRef.current = initialWindow;
        setLoadedRange(initialWindow);
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to load guide data:', e);
      } finally {
        if (!cancelled) {
          setIsChannelsLoading(false);
          setIsProgramsLoading(false);
        }
      }
    };

    loadGuideData();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowAllGroups, channelGroups, searchQuery, selectedProfileId, groupRefetchKey]);

  // Apply filters when search, group, or profile changes
  const filteredChannels = useMemo(() => {
    if (!guideChannels.length) return [];

    return filterGuideChannels(
      guideChannels,
      searchQuery,
      selectedGroupId,
      selectedProfileId,
      profiles
    );
  }, [
    searchQuery,
    selectedGroupId,
    selectedProfileId,
    guideChannels,
    profiles,
  ]);

  // Groups present on the loaded (profile-scoped) channels only.
  const groupOptions = useMemo(() => {
    const opts = getGroupOptions(channelGroups, guideChannels);
    if (!allowAllGroups) {
      return opts.filter((o) => o.value !== 'all');
    }
    return opts;
  }, [channelGroups, guideChannels, allowAllGroups]);

  // Drop a selected group that is no longer in the visible set (profile change).
  useEffect(() => {
    if (selectedGroupId === 'all') return;
    const stillVisible = groupOptions.some(
      (o) => o.value === String(selectedGroupId)
    );
    if (!stillVisible) {
      setSelectedGroupId('all');
    }
  }, [groupOptions, selectedGroupId]);

  // DISPATCHARR_GUIDE_FIXED_ORIGIN_R94
  // The timeline origin is fixed for the life of the page: 8 days before the
  // moment it opened (the deepest archive TiVi.bg offers) through 8 days after
  // (A1 publishes about a week ahead). It used to be the loaded window's left
  // edge, so every fetch of older programmes moved the origin and the guide
  // had to shift scrollLeft by the same amount to stay put (R74) -- and when
  // that correction landed inside an iOS momentum scroll the viewport was
  // thrown hours away from where the finger had left it. Now nothing on screen
  // ever moves: programmes are still fetched in windows (loadedRange), and
  // hours not fetched yet simply render empty until they arrive.
  const timelineBounds = useMemo(() => {
    const anchorMs = convertToMs(getNow());
    const startDt = startDate
      ? initializeTime(startDate)
      : initializeTime(anchorMs - ORIGIN_LOOKBACK_MS);
    const endDt = endDate
      ? initializeTime(endDate)
      : initializeTime(anchorMs + ORIGIN_FORWARD_MS);
    return { start: startOfHour(startDt), end: startOfHour(endDt) };
  }, [startDate, endDate]);
  const start = timelineBounds.start;
  const end = timelineBounds.end;

  // Pre-compute timeline origin in ms for horizontal culling in GuideRow
  const timelineStartMs = useMemo(() => convertToMs(start), [start]);

  const channelIdByTvgId = useMemo(
    () => buildChannelIdMap(guideChannels, tvgsById, epgs),
    [guideChannels, tvgsById, epgs]
  );

  // Local map of channel id -> channel object for quick lookup
  const channelById = useMemo(() => {
    const map = new Map();
    for (const ch of guideChannels) {
      if (ch && ch.id !== undefined && ch.id !== null) {
        map.set(ch.id, ch);
      }
    }
    return map;
  }, [guideChannels]);

  const programsByChannelId = useMemo(
    () => mapProgramsByChannel(programs, channelIdByTvgId),
    [programs, channelIdByTvgId, now]
  );

  const recordingsByProgramId = useMemo(
    () => mapRecordingsByProgramId(recordings),
    [recordings]
  );

  const rowHeights = useMemo(
    () => computeRowHeights(filteredChannels),
    [filteredChannels]
  );

  const getItemSize = useCallback(
    (index) => rowHeights[index] ?? PROGRAM_HEIGHT,
    [rowHeights]
  );

  const { timeFormat, dateFormat, dateYearFormat } = useDateTimeFormat();

  // Format day label using relative terms when possible (Today, Tomorrow, etc)
  const formatDayLabel = useCallback(
    (time) => formatTime(time, dateFormat),
    [dateFormat]
  );

  // Hourly marks with day labels
  const hourTimeline = useMemo(
    () => calculateHourTimeline(start, end, formatDayLabel),
    [start, end, formatDayLabel]
  );

  useEffect(() => {
    const node = guideRef.current;
    if (!node) return undefined;

    const handleScroll = () => {
      // DISPATCHARR_GUIDE_SCROLL_BACK_R74: every scroll, including the tail of
      // an iOS flick, marks the guide as moving.
      lastScrollAtRef.current = Date.now();
      if (isSyncingScroll.current) {
        return;
      }

      const { scrollLeft } = node;

      // Always sync if timeline is out of sync, even if ref matches
      if (
        timelineRef.current &&
        timelineRef.current.scrollLeft !== scrollLeft
      ) {
        isSyncingScroll.current = true;
        timelineRef.current.scrollLeft = scrollLeft;
        guideScrollLeftRef.current = scrollLeft;
        updateNowLine();
        requestAnimationFrame(() => {
          isSyncingScroll.current = false;
        });
      } else if (scrollLeft !== guideScrollLeftRef.current) {
        // Update ref even if timeline was already synced
        guideScrollLeftRef.current = scrollLeft;
        updateNowLine();
      }
    };

    node.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      node.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Update "now" every 60 seconds (on a 24h guide, per-second is imperceptible)
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(getNow());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Recover from browser tab throttling — when the user returns to the tab,
  // force-refresh "now" so programs are remapped with current time values.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setNow(getNow());
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Pixel offset for the "now" vertical line
  const nowPosition = useMemo(
    () => calculateNowPosition(now, start, end),
    [now, start, end]
  );

  // Keep timelineStartMs in a ref for real-time now-line calculation
  const timelineStartMsRef = useRef(timelineStartMs);
  timelineStartMsRef.current = timelineStartMs;

  // Update the now-line DOM element directly (no React re-render)
  // Uses Date.now() for sub-minute precision to stay aligned with progress bars
  const updateNowLine = useCallback(() => {
    if (nowLineRef.current) {
      const nowPx = (Date.now() - timelineStartMsRef.current) * PX_PER_MS;
      nowLineRef.current.style.left = `${Math.round(nowPx + CHANNEL_WIDTH - guideScrollLeftRef.current)}px`;
    }
    // Debounce horizontal culling update — fires 150ms after scrolling stops
    if (scrollDebounceRef.current) {
      clearTimeout(scrollDebounceRef.current);
    }
    scrollDebounceRef.current = setTimeout(() => {
      setSettledScrollLeft(guideScrollLeftRef.current);
    }, 150);
  }, []);

  // Advance now-line and all progress bars every second via direct DOM updates
  // (no React re-renders). Using a single Date.now() per tick keeps everything in sync.
  // Pixel positions are rounded to avoid sub-pixel rendering artifacts (line pulsing).
  // Progress bar scaleX is adjusted to account for the card's gap offset so the
  // fill edge aligns exactly with the now line on the timeline grid.
  useEffect(() => {
    const interval = setInterval(() => {
      const nowMs = Date.now();
      if (nowLineRef.current) {
        const nowPx = (nowMs - timelineStartMsRef.current) * PX_PER_MS;
        nowLineRef.current.style.left = `${Math.round(nowPx + CHANNEL_WIDTH - guideScrollLeftRef.current)}px`;
      }
      document.querySelectorAll('.guide-progress-fill').forEach((el) => {
        const startMs = Number(el.dataset.startMs);
        const endMs = Number(el.dataset.endMs);
        const durationMs = endMs - startMs;
        if (durationMs <= 0) return;
        el.style.transform = `scaleX(${calcProgressPct(nowMs, startMs, durationMs)})`;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Clear debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current);
      }
    };
  }, []);

  // Sync now-line whenever nowPosition changes (every 60s)
  useEffect(() => {
    updateNowLine();
  }, [nowPosition, updateNowLine]);

  useEffect(() => {
    const tvGuide = tvGuideRef.current;

    if (!tvGuide) return undefined;

    // DISPATCHARR_GUIDE_WHEEL_AXIS_R93: only a gesture whose dominant axis is
    // horizontal is ours; vertical wheel movement is the browser's, untouched.
    // The old test was `deltaX !== 0`, and Firefox (which, unlike Chrome and
    // Safari, does not lock a trackpad gesture to one axis) reports a small
    // deltaX on nearly every vertical two-finger scroll -- each of those
    // events was preventDefault'ed, its vertical component was lost, and the
    // grid barely moved down while juddering sideways. See utils/guideWheel.js.
    const wheelGesture = { axis: null, lastAt: 0 };

    const handleContainerWheel = (event) => {
      const guide = guideRef.current;
      const timeline = timelineRef.current;

      if (!guide) {
        return;
      }

      const { dx, dy } = normalizeWheelDelta(event, guide);
      const axis = wheelGestureAxis(
        wheelGesture,
        dx,
        dy,
        event.shiftKey,
        performance.now()
      );
      if (axis !== 'x') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const delta = horizontalWheelAmount(dx, dy, event.shiftKey);
      if (delta === 0) {
        return;
      }
      const newScrollLeft = guide.scrollLeft + delta;

      // Set both guide and timeline scroll positions
      if (typeof guide.scrollTo === 'function') {
        guide.scrollTo({ left: newScrollLeft, behavior: 'auto' });
      } else {
        guide.scrollLeft = newScrollLeft;
      }

      // Also sync timeline immediately
      if (timeline) {
        if (typeof timeline.scrollTo === 'function') {
          timeline.scrollTo({ left: newScrollLeft, behavior: 'auto' });
        } else {
          timeline.scrollLeft = newScrollLeft;
        }
      }

      // Update the ref to keep state in sync
      guideScrollLeftRef.current = newScrollLeft;
      updateNowLine();
    };

    tvGuide.addEventListener('wheel', handleContainerWheel, {
      passive: false,
      capture: true,
    });

    return () => {
      tvGuide.removeEventListener('wheel', handleContainerWheel, {
        capture: true,
      });
    };
  }, []);

  // Fallback: continuously monitor for any scroll changes
  useEffect(() => {
    let rafId = null;
    let lastCheck = 0;

    const checkSync = (timestamp) => {
      // Throttle to check every 100ms instead of every frame
      if (timestamp - lastCheck > 100) {
        const guide = guideRef.current;
        const timeline = timelineRef.current;

        if (guide && timeline && guide.scrollLeft !== timeline.scrollLeft) {
          timeline.scrollLeft = guide.scrollLeft;
          guideScrollLeftRef.current = guide.scrollLeft;
          updateNowLine();
        }
        lastCheck = timestamp;
      }

      rafId = requestAnimationFrame(checkSync);
    };

    rafId = requestAnimationFrame(checkSync);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    const tvGuide = tvGuideRef.current;
    if (!tvGuide) return;

    let lastTouchX = null;
    let isTouching = false;
    let rafId = null;
    let lastScrollLeft = 0;
    let stableFrames = 0;

    const syncScrollPositions = () => {
      const guide = guideRef.current;
      const timeline = timelineRef.current;

      if (!guide || !timeline) return false;

      const currentScroll = guide.scrollLeft;

      // Check if scroll position has changed
      if (currentScroll !== lastScrollLeft) {
        timeline.scrollLeft = currentScroll;
        guideScrollLeftRef.current = currentScroll;
        updateNowLine();
        // DISPATCHARR_GUIDE_SCROLL_BACK_R74: iOS delivers scroll events
        // sparsely during a flick; this poll sees every frame of it.
        lastScrollAtRef.current = Date.now();
        lastScrollLeft = currentScroll;
        stableFrames = 0;
        return true; // Still scrolling
      } else {
        stableFrames++;
        return stableFrames < 10; // Continue for 10 stable frames to catch late updates
      }
    };

    const startPolling = () => {
      if (rafId) return; // Already polling

      const poll = () => {
        const shouldContinue = isTouching || syncScrollPositions();

        if (shouldContinue) {
          rafId = requestAnimationFrame(poll);
        } else {
          rafId = null;
        }
      };

      rafId = requestAnimationFrame(poll);
    };

    // DISPATCHARR_GUIDE_SCROLL_BACK_R74: the guide is a natively scrollable
    // element, so panning it also moved it by hand here -- the content
    // travelled twice as far as the finger, and the flick that followed was
    // the browser's alone. Let the browser scroll it, and only step in if the
    // finger moves horizontally while the container does not, which is how a
    // runtime that refuses to pan this element would look.
    let touchStartX = null;
    let touchStartY = null;
    let touchStartScrollLeft = 0;
    let manualPanNeeded = false;

    const handleTouchStart = (e) => {
      if (e.touches.length === 1) {
        const guide = guideRef.current;
        if (guide) {
          lastTouchX = e.touches[0].clientX;
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
          touchStartScrollLeft = guide.scrollLeft;
          lastScrollLeft = guide.scrollLeft;
          isTouching = true;
          touchActiveRef.current = true;
          manualPanNeeded = false;
          stableFrames = 0;
          startPolling();
        }
      }
    };

    const handleTouchMove = (e) => {
      if (!isTouching || e.touches.length !== 1) return;
      const guide = guideRef.current;
      if (!guide) return;

      const touchX = e.touches[0].clientX;
      const deltaX = lastTouchX - touchX;
      lastTouchX = touchX;
      lastScrollAtRef.current = Date.now();

      if (!manualPanNeeded && touchStartX != null) {
        const movedX = Math.abs(touchStartX - touchX);
        const movedY = Math.abs(touchStartY - e.touches[0].clientY);
        // A clearly horizontal drag that has not moved the container at all
        // means native panning is not happening here.
        if (movedX > 24 && movedX > movedY && guide.scrollLeft === touchStartScrollLeft) {
          manualPanNeeded = true;
        }
      }

      if (manualPanNeeded && Math.abs(deltaX) > 0) {
        guide.scrollLeft += deltaX;
      }
    };

    const handleTouchEnd = () => {
      isTouching = false;
      touchActiveRef.current = false;
      lastTouchX = null;
      touchStartX = null;
      touchStartY = null;
      lastScrollAtRef.current = Date.now();
      // Polling continues until scroll stabilizes
    };

    tvGuide.addEventListener('touchstart', handleTouchStart, { passive: true });
    // DISPATCHARR_GUIDE_FIXED_ORIGIN_R94: nothing here calls preventDefault,
    // so the listener is passive and iOS never waits for it mid-pan.
    tvGuide.addEventListener('touchmove', handleTouchMove, { passive: true });
    tvGuide.addEventListener('touchend', handleTouchEnd, { passive: true });
    tvGuide.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      tvGuide.removeEventListener('touchstart', handleTouchStart);
      tvGuide.removeEventListener('touchmove', handleTouchMove);
      tvGuide.removeEventListener('touchend', handleTouchEnd);
      tvGuide.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, []);

  // DISPATCHARR_GUIDE_SCROLL_BACK_R74
  // Loading older programmes prepends hours to the timeline, which pushes
  // everything already on screen to the right; the scroll position has to move
  // by the same amount or the viewport lands hours away from where the user
  // was. Doing that while a flick is still running loses the correction to the
  // momentum, which is what threw the guide far back on iOS. Hold the update
  // until the guide is still: the programmes are already fetched, so the wait
  // costs nothing visible.
  const scrollIsSettled = useCallback(
    () => !touchActiveRef.current && Date.now() - lastScrollAtRef.current > 150,
    []
  );

  const scheduleWindowApply = useCallback(
    (apply) => {
      // Forward and backward extends can be waiting at the same time, so this
      // is a queue: dropping one would leave its direction marked in-flight
      // and that edge would never load again.
      pendingWindowApplyRef.current.push(apply);

      const drain = () => {
        pendingWindowTimerRef.current = null;
        const queued = pendingWindowApplyRef.current;
        pendingWindowApplyRef.current = [];
        queued.forEach((run) => run());
      };

      if (scrollIsSettled()) {
        if (pendingWindowTimerRef.current) {
          clearTimeout(pendingWindowTimerRef.current);
        }
        drain();
        return;
      }

      if (pendingWindowTimerRef.current) return; // a tick is already waiting

      // Never hold an update hostage to a scroll that refuses to settle. An
      // iOS flick can coast for several seconds; 2.5 s used to expire inside
      // it and the update landed mid-momentum.
      const deadline = Date.now() + 6000;
      const tick = () => {
        pendingWindowTimerRef.current = null;
        if (!pendingWindowApplyRef.current.length) return;
        if (scrollIsSettled() || Date.now() > deadline) {
          drain();
          return;
        }
        pendingWindowTimerRef.current = setTimeout(tick, 80);
      };
      pendingWindowTimerRef.current = setTimeout(tick, 80);
    },
    [scrollIsSettled]
  );

  useEffect(
    () => () => {
      if (pendingWindowTimerRef.current) {
        clearTimeout(pendingWindowTimerRef.current);
      }
    },
    []
  );

  const syncScrollLeft = useCallback((nextLeft, behavior = 'auto') => {
    const guideNode = guideRef.current;
    const timelineNode = timelineRef.current;

    isSyncingScroll.current = true;

    if (guideNode) {
      if (typeof guideNode.scrollTo === 'function') {
        guideNode.scrollTo({ left: nextLeft, behavior });
      } else {
        guideNode.scrollLeft = nextLeft;
      }
    }

    if (timelineNode) {
      if (typeof timelineNode.scrollTo === 'function') {
        timelineNode.scrollTo({ left: nextLeft, behavior });
      } else {
        timelineNode.scrollLeft = nextLeft;
      }
    }

    guideScrollLeftRef.current = nextLeft;
    updateNowLine();

    requestAnimationFrame(() => {
      isSyncingScroll.current = false;
    });
  }, []);

  // DISPATCHARR_GUIDE_JUMP_TO_NOW_R75
  // The clock button used a smooth scroll, which a phone cancels at the first
  // touch and which loses to momentum that is still unwinding -- so the button
  // often appeared to do nothing. Jump straight there instead, then hold the
  // position for a few frames until it sticks, and stop as soon as the user
  // takes over.
  const jumpScrollLeft = useCallback(
    (target) => {
      syncScrollLeft(target, 'auto');

      const deadline = Date.now() + 600;
      let settledFrames = 0;
      const hold = () => {
        const guideNode = guideRef.current;
        if (!guideNode || touchActiveRef.current) return;
        if (Math.abs(guideNode.scrollLeft - target) > 2) {
          syncScrollLeft(target, 'auto');
          settledFrames = 0;
        } else {
          settledFrames += 1;
        }
        if (settledFrames < 3 && Date.now() < deadline) {
          requestAnimationFrame(hold);
        }
      };
      requestAnimationFrame(hold);
    },
    [syncScrollLeft]
  );

  // Holds the scroll position to restore after a filter-induced remount.
  // null means "use the default scroll-to-now on first load".
  const savedScrollLeftRef = useRef(null);

  // When channels become empty (filter transition unmounts the list), save the
  // current scroll position so we can restore it once new channels arrive.
  // Only save if the initial scroll has already happened — otherwise the saved
  // position would be 0 (the DOM default) and we'd skip the scroll-to-now.
  useEffect(() => {
    if (filteredChannels.length === 0) {
      if (initialScrollComplete) {
        savedScrollLeftRef.current = guideScrollLeftRef.current;
      }
      setInitialScrollComplete(false);
    }
  }, [filteredChannels.length, initialScrollComplete]);

  // Scroll on initial load, or restore saved position after a filter transition.
  // Guard with guideRef.current — the VariableSizeList outer div is null while
  // unmounted, so we must wait until it remounts before calling syncScrollLeft.
  useEffect(() => {
    if (programs.length > 0 && !initialScrollComplete && guideRef.current) {
      if (pendingNowJumpRef.current) {
        // DISPATCHARR_GUIDE_JUMP_TO_NOW_R75: the clock button reloaded the
        // window around now; this is the jump it was waiting for.
        pendingNowJumpRef.current = false;
        jumpScrollLeft(calculateScrollPosition(now, start));
      } else if (savedScrollLeftRef.current !== null) {
        // Restore where the user was before the filter change
        syncScrollLeft(savedScrollLeftRef.current);
        savedScrollLeftRef.current = null;
      } else {
        // Genuine first load — scroll to current time
        syncScrollLeft(calculateScrollPosition(now, start));
      }
      setInitialScrollComplete(true);
    }
  }, [
    programs,
    start,
    now,
    initialScrollComplete,
    syncScrollLeft,
    jumpScrollLeft,
    filteredChannels.length,
  ]);

  // Warm-ahead: when the viewport nears a loaded edge, fetch the adjacent
  // 12h chunk (programs only). Soft-cull when span exceeds KEEP_MS.
  const extendProgramWindow = useCallback(
    async (direction, chunkMs = CHUNK_MS) => {
      const range = loadedRangeRef.current;
      if (!range || !initialScrollComplete) return;
      if (extendInflightRef.current[direction]) return;

      const chunk =
        direction === 'forward'
          ? nextChunkForward(range.endMs, chunkMs)
          : nextChunkBackward(range.startMs, chunkMs);

      const epoch = windowEpochRef.current;
      extendInflightRef.current[direction] = true;
      let applied = false;
      try {
        const params = new URLSearchParams();
        const profileParam = programProfileRef.current;
        if (profileParam) {
          params.set('channel_profile_id', profileParam);
        }
        appendWindowParams(params, chunk.startMs, chunk.endMs);
        const incoming = await fetchPrograms(params);

        if (windowEpochRef.current !== epoch) return;

        const currentRange = loadedRangeRef.current;
        if (!currentRange) return;

        // DISPATCHARR_GUIDE_SCROLL_BACK_R74 / _FIXED_ORIGIN_R94: the merge
        // re-renders every visible row, so it still waits for the scroll to be
        // still (or the deadline). With the fixed origin nothing moves
        // sideways any more, so no scrollLeft correction follows it.
        const applyWindow = () => {
          if (windowEpochRef.current !== epoch) return;
          const guideNode = guideRef.current;
          const viewportWidth = guideNode?.clientWidth || guideWidth || 0;
          let finalRange = null;

          // Functional merge so concurrent forward/backward completes do not
          // clobber each other. Re-read loadedRange inside the updater for the
          // same reason.
          setPrograms((prev) => {
            const base = loadedRangeRef.current || currentRange;
            const nextRange =
              direction === 'forward'
                ? {
                    startMs: base.startMs,
                    endMs: Math.max(base.endMs, chunk.endMs),
                  }
                : {
                    startMs: Math.min(base.startMs, chunk.startMs),
                    endMs: base.endMs,
                  };

            const view = viewportTimeRange(
              timelineStartMs,
              Math.max(0, guideScrollLeftRef.current),
              viewportWidth,
              PX_PER_MS
            );

            const withIncoming = mergeProgramsById(prev, incoming || []);
            const culled = cullProgramsToKeepWindow(
              withIncoming,
              view.centerMs,
              KEEP_MS,
              nextRange.startMs,
              nextRange.endMs,
              convertToMs
            );
            finalRange = {
              startMs: culled.rangeStartMs,
              endMs: culled.rangeEndMs,
            };
            loadedRangeRef.current = finalRange;
            return culled.programs;
          });

          if (!finalRange) return;
          setLoadedRange(finalRange);
        };

        scheduleWindowApply(() => {
          try {
            applyWindow();
          } finally {
            extendInflightRef.current[direction] = false;
          }
        });
        applied = true;
      } catch (e) {
        console.error(`Failed to extend guide window (${direction}):`, e);
      } finally {
        // A scheduled apply owns the flag until it runs, so the same chunk is
        // not fetched again while it waits for the scroll to settle.
        if (!applied) extendInflightRef.current[direction] = false;
      }
    },
    [guideWidth, initialScrollComplete, scheduleWindowApply, timelineStartMs]
  );

  // DISPATCHARR_GUIDE_WARMUP_R94: once the guide is on screen, quietly fetch
  // the previous 24 h and the next 12 h, so a scroll back through the day or
  // ahead through the evening meets programmes that are already there instead
  // of a fetch at the edge. Once per loaded window (epoch). Declared before
  // the edge prefetch below so it claims the in-flight slots first.
  const warmedEpochRef = useRef(0);
  useEffect(() => {
    if (!initialScrollComplete || !loadedRange) return;
    if (warmedEpochRef.current === windowEpochRef.current) return;
    warmedEpochRef.current = windowEpochRef.current;
    void extendProgramWindow('backward', WARMUP_BACK_MS);
    void extendProgramWindow('forward', WARMUP_FORWARD_MS);
  }, [initialScrollComplete, loadedRange, extendProgramWindow]);

  useEffect(() => {
    if (!initialScrollComplete || !loadedRange) return;

    const range = loadedRangeRef.current || loadedRange;
    const guideNode = guideRef.current;
    const viewportWidth = guideNode?.clientWidth || guideWidth || 0;
    // Prefer the live ref: settledScrollLeft can lag behind syncScrollLeft
    // on first paint and would falsely trigger a backward prefetch.
    const scrollLeft = guideScrollLeftRef.current;
    const view = viewportTimeRange(
      timelineStartMs,
      scrollLeft,
      viewportWidth,
      PX_PER_MS
    );

    if (shouldPrefetchForward(view.endMs, range.endMs)) {
      extendProgramWindow('forward');
    }
    // DISPATCHARR_GUIDE_FIXED_ORIGIN_R94: purely time-based. The old pixel
    // guard assumed the timeline's left edge was the loaded edge; with the
    // fixed origin the viewport is days from the physical edge, so only the
    // distance to the loaded window can say when to fetch more.
    if (shouldPrefetchBackward(view.startMs, range.startMs)) {
      extendProgramWindow('backward');
    }
  }, [
    settledScrollLeft,
    loadedRange,
    timelineStartMs,
    guideWidth,
    initialScrollComplete,
    extendProgramWindow,
  ]);

  const findChannelByTvgId = useCallback(
    (tvgId) => matchChannelByTvgId(channelIdByTvgId, channelById, tvgId),
    [channelById, channelIdByTvgId]
  );

  const openRecordChoice = useCallback(
    async (program, channel) => {
      setRecordChoiceProgram(program);
      setRecordChoiceChannel(channel);
      setRecordChoiceOpen(true);
      try {
        const rules = await fetchRules();
        const tvgRecord = channel?.epg_data_id
          ? tvgsById[channel.epg_data_id]
          : null;
        const rule = getRuleByProgram(rules, program, tvgRecord?.epg_source);
        setExistingRuleMode(rule ? rule.mode : null);
        setExistingRule(rule || null);
      } catch (error) {
        console.warn('Failed to fetch series rules metadata', error);
      }

      setRecordingForProgram(recordingsByProgramId.get(program.id) || null);
    },
    [recordingsByProgramId, tvgsById]
  );

  const recordOne = useCallback(async (program, channel) => {
    if (!channel) {
      showNotification({
        title: 'Unable to schedule recording',
        message: 'No channel found for this program.',
        color: 'red.6',
      });
      return;
    }

    await createRecording({
      channel: `${channel.id}`,
      start_time: program.start_time,
      end_time: program.end_time,
      custom_properties: { program },
    });
    showNotification({ title: 'Recording scheduled' });
  }, []);

  const saveSeriesRule = useCallback(
    async (program, mode) => {
      const tvgRecord = recordChoiceChannel?.epg_data_id
        ? tvgsById[recordChoiceChannel.epg_data_id]
        : null;
      await createSeriesRule({
        tvg_id: program.tvg_id,
        mode,
        title: program.title,
        ...(tvgRecord?.epg_source
          ? { epg_source_id: tvgRecord.epg_source }
          : {}),
      });
      await evaluateSeriesRulesByTvgId(program.tvg_id);
      // recordings_refreshed WS event triggers the debounced fetchRecordings()
      showNotification({
        title: mode === 'new' ? 'Record new episodes' : 'Record all episodes',
      });
    },
    [recordChoiceChannel, tvgsById]
  );

  const openRules = useCallback(async () => {
    setRulesOpen(true);
    try {
      const r = await fetchRules();
      setRules(r);
    } catch (error) {
      console.warn('Failed to load series rules', error);
    }
  }, []);

  const showVideo = useVideoStore((s) => s.showVideo);

  const handleLogoClick = useCallback(
    (channel, event) => {
      event.stopPropagation();

      showVideo(getShowVideoUrl(channel, env_mode), 'live', {
        name: channel.name,
      });
    },
    [env_mode, showVideo]
  );

  // DISPATCHARR_GUIDE_EXACT_LIVE_STATE_R8_8_24
  const handleProgramClick = useCallback(
    (program, channel, isGuideLive, event) => {
      event.stopPropagation();
      // DISPATCHARR_GUIDE_WATCH_LIVE_ROW_CHANNEL_R8_8_25
      // The green/current EPG card already has the authoritative `isLive`.
      // Carry both that flag and the exact row Channel into the selected object
      // so ProgramDetailModal never needs to reconstruct the row relationship.
      const resolvedChannel =
        channel || findChannelByTvgId(program.tvg_id) || null;
      const exactGuideLive = Boolean(
        isGuideLive || program.isLive || program.is_live
      );
      const selectedProgramForModal = {
        ...program,
        isLive: exactGuideLive,
        is_live: exactGuideLive || Boolean(program.is_live),
        __dispatcharrGuideChannel: resolvedChannel,
      };
      setSelectedProgram(selectedProgramForModal);
      setSelectedProgramIsLive(exactGuideLive);
      setSelectedChannel(resolvedChannel);
      setRecordingForProgram(recordingsByProgramId.get(program.id) || null);
    },
    [findChannelByTvgId, recordingsByProgramId]
  );

  const handleCloseModal = useCallback(() => {
    setSelectedProgram(null);
    setSelectedChannel(null);
    setRecordingForProgram(null);
  }, []);

  const scrollToNow = useCallback(() => {
    const nowMs = convertToMs(now);
    const range = loadedRangeRef.current;
    const nowOutsideLoaded =
      nowPosition < 0 ||
      !range ||
      nowMs < range.startMs ||
      nowMs >= range.endMs;

    if (!nowOutsideLoaded) {
      jumpScrollLeft(calculateScrollPosition(now, start));
      return;
    }
    pendingNowJumpRef.current = true;

    // Soft-cull (or a far scroll) dropped "now" out of the loaded window.
    // Reload the default around-now range, then let the scroll effect jump.
    (async () => {
      const epoch = ++windowEpochRef.current;
      extendInflightRef.current = { forward: false, backward: false };
      const initialWindow = getInitialWindow(nowMs);
      try {
        const params = new URLSearchParams();
        const profileParam = programProfileRef.current;
        if (profileParam) {
          params.set('channel_profile_id', profileParam);
        }
        appendWindowParams(
          params,
          initialWindow.startMs,
          initialWindow.endMs
        );
        const programData = await fetchPrograms(params);
        if (windowEpochRef.current !== epoch) return;

        loadedRangeRef.current = initialWindow;
        savedScrollLeftRef.current = null;
        setLoadedRange(initialWindow);
        setPrograms(programData || []);
        setInitialScrollComplete(false);
      } catch (e) {
        pendingNowJumpRef.current = false;
        console.error('Failed to reload guide window around now:', e);
      }
    })();
  }, [now, nowPosition, start, jumpScrollLeft]);

  const handleTimelineScroll = useCallback(() => {
    if (!timelineRef.current || isSyncingScroll.current) {
      return;
    }

    const nextLeft = timelineRef.current.scrollLeft;
    if (nextLeft === guideScrollLeftRef.current) {
      return;
    }

    guideScrollLeftRef.current = nextLeft;
    updateNowLine();

    isSyncingScroll.current = true;
    if (guideRef.current) {
      if (typeof guideRef.current.scrollTo === 'function') {
        guideRef.current.scrollTo({ left: nextLeft });
      } else {
        guideRef.current.scrollLeft = nextLeft;
      }
    }

    requestAnimationFrame(() => {
      isSyncingScroll.current = false;
    });
  }, []);

  const handleTimelineWheel = useCallback((event) => {
    if (!timelineRef.current) {
      return;
    }

    event.preventDefault();
    const scrollAmount = event.shiftKey ? 250 : 125;
    const delta = event.deltaY > 0 ? scrollAmount : -scrollAmount;
    timelineRef.current.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);

  const handleTimeClick = useCallback(
    (clickedTime, event) => {
      syncScrollLeft(
        calculateScrollPositionByTimeClick(event, clickedTime, start),
        'smooth'
      );
    },
    [start, syncScrollLeft]
  );
  const renderProgram = useCallback(
    (program, channelStart = start, channel = null) => {
      const {
        programStart,
        programEnd,
        startMs: programStartMs,
        endMs: programEndMs,
        isLive,
        isPast,
      } = program;

      const startOffsetMinutes =
        (programStartMs - convertToMs(channelStart)) / 60000;
      const durationMinutes = (programEndMs - programStartMs) / 60000;
      const leftPx =
        (startOffsetMinutes / MINUTE_INCREMENT) * MINUTE_BLOCK_WIDTH;

      const widthPx =
        (durationMinutes / MINUTE_INCREMENT) * MINUTE_BLOCK_WIDTH -
        PROGRAM_GAP_PX * 2;

      const recording = recordingsByProgramId.get(program.id);
      // DISPATCHARR_GUIDE_WATCH_LIVE_ROW_CHANNEL_R8_8_25
      const mappedChannelId =
        channelIdByTvgId instanceof Map
          ? channelIdByTvgId.get(program.tvg_id)
          : channelIdByTvgId?.[program.tvg_id];
      const programChannel =
        channel ||
        (mappedChannelId != null ? channelById.get(mappedChannelId) : null) ||
        findChannelByTvgId(program.tvg_id);

      const programStartInView = leftPx + PROGRAM_GAP_PX;
      const programEndInView = leftPx + PROGRAM_GAP_PX + widthPx;
      const viewportLeft = guideScrollLeftRef.current;
      const startsBeforeView = programStartInView < viewportLeft;
      const extendsIntoView = programEndInView > viewportLeft;

      let textOffsetLeft = 0;
      if (startsBeforeView && extendsIntoView) {
        const visibleStart = Math.max(viewportLeft - programStartInView, 0);
        const maxOffset = widthPx - 200;
        textOffsetLeft = Math.min(visibleStart, maxOffset);
      }

      const seasonEpisodeLabel = formatSeasonEpisode(
        program.season,
        program.episode
      );
      return (
        <Box
          className="guide-program-container"
          key={`${channel?.id || 'unknown'}-${program.id || `${program.tvg_id}-${program.start_time}`}`}
          style={{ cursor: 'pointer', zIndex: 5 }}
          pos="absolute"
          left={leftPx + PROGRAM_GAP_PX}
          top={0}
          w={widthPx}
          h={PROGRAM_HEIGHT - 4}
          onClick={(event) =>
            handleProgramClick(program, programChannel, isLive, event)
          }
        >
          <Paper
            elevation={2}
            className={`guide-program ${isLive ? 'live' : isPast ? 'past' : 'not-live'}`}
            style={{
              overflow: 'hidden',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              backgroundColor: isLive
                ? '#18181B'
                : isPast
                  ? '#27272A'
                  : '#2c5282',
            }}
            w={'100%'}
            h={'100%'}
            pos="relative"
            display={'flex'}
            p={8}
            c={isPast ? '#a0aec0' : '#fff'}
          >
            <Box
              style={{
                transform: `translateX(${textOffsetLeft}px)`,
                transition: 'transform 0.1s ease-out',
                display: 'flex',
                flexDirection: 'column',
                justifyContent:
                  program.sub_title || program.description
                    ? 'space-between'
                    : 'flex-start',
                flex: '1 1 0',
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              {/* Row 1: Title with recording indicator */}
              <Text
                component="div"
                size="md"
                style={{
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
                fw={'bold'}
              >
                <Group gap="xs" wrap="nowrap">
                  {recording && (
                    <div
                      style={{
                        borderRadius: '50%',
                        width: '10px',
                        height: '10px',
                        display: 'flex',
                        flexShrink: 0,
                        backgroundColor: 'red',
                      }}
                    ></div>
                  )}
                  {program.title}
                </Group>
              </Text>

              {/* Row 2: S/E badge + Subtitle or description fallback */}
              {(seasonEpisodeLabel ||
                program.sub_title ||
                program.description) && (
                <Group
                  gap={4}
                  wrap="nowrap"
                  style={{ overflow: 'hidden', minWidth: 0 }}
                >
                  {seasonEpisodeLabel && (
                    <Badge
                      size="xs"
                      variant="light"
                      color="cyan"
                      styles={{
                        root: {
                          backgroundColor: 'rgba(20,90,110,0.55)',
                          flexShrink: 0,
                        },
                      }}
                    >
                      {seasonEpisodeLabel}
                    </Badge>
                  )}
                  {(program.sub_title || program.description) && (
                    <Text
                      size="xs"
                      fs={program.sub_title ? 'italic' : 'normal'}
                      style={{
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        minWidth: 0,
                      }}
                      c={isPast ? '#718096' : '#e2e8f0'}
                    >
                      {program.sub_title || program.description}
                    </Text>
                  )}
                </Group>
              )}

              {/* Row 3: Time + LIVE/NEW badges */}
              <Group gap={4} wrap="nowrap" style={{ overflow: 'hidden' }}>
                <Text
                  size="sm"
                  style={{
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {format(programStart, timeFormat)} -{' '}
                  {format(programEnd, timeFormat)}
                </Text>
                {program.is_live && (
                  <Badge
                    size="xs"
                    variant="light"
                    color="red"
                    styles={{
                      root: {
                        backgroundColor: 'rgba(120,20,20,0.55)',
                        flexShrink: 0,
                      },
                    }}
                  >
                    LIVE
                  </Badge>
                )}
                {program.is_new && (
                  <Badge
                    size="xs"
                    variant="light"
                    color="green"
                    styles={{
                      root: {
                        backgroundColor: 'rgba(20,100,20,0.55)',
                        flexShrink: 0,
                      },
                    }}
                  >
                    NEW
                  </Badge>
                )}
              </Group>
            </Box>

            {/* Progress bar for currently-airing programs — updated every second via DOM */}
            {isLive &&
              (() => {
                const durationMs = programEndMs - programStartMs;
                if (durationMs <= 0) return null;
                const initialPct = calcProgressPct(
                  Date.now(),
                  programStartMs,
                  durationMs
                );
                return (
                  <Box
                    pos="absolute"
                    bottom={0}
                    left={0}
                    right={0}
                    h={4}
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '0 0 8px 8px',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      className="guide-progress-fill"
                      data-start-ms={programStartMs}
                      data-end-ms={programEndMs}
                      h="100%"
                      style={{
                        width: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        borderRadius: '0 0 0 8px',
                        transformOrigin: 'left',
                        transform: `scaleX(${initialPct})`,
                      }}
                    />
                  </Box>
                );
              })()}
          </Paper>
        </Box>
      );
    },
    [handleProgramClick, recordingsByProgramId, start, timeFormat]
  );

  const contentWidth = useMemo(
    () => hourTimeline.length * HOUR_WIDTH + CHANNEL_WIDTH,
    [hourTimeline]
  );

  const virtualizedHeight = useMemo(() => guideHeight || 600, [guideHeight]);

  const virtualizedWidth = useMemo(() => {
    if (guideWidth) {
      return guideWidth;
    }
    if (typeof window !== 'undefined') {
      return Math.min(window.innerWidth, contentWidth);
    }
    return contentWidth;
  }, [guideWidth, contentWidth]);

  const itemKey = useCallback(
    (index) => filteredChannels[index]?.id ?? index,
    [filteredChannels]
  );

  const listData = useMemo(
    () => ({
      filteredChannels,
      programsByChannelId,
      rowHeights,
      renderProgram,
      handleLogoClick,
      contentWidth,
      guideScrollLeftRef,
      viewportWidth:
        guideWidth ||
        (typeof window !== 'undefined' ? window.innerWidth : 1200),
      timelineStartMs,
      settledScrollLeft, // triggers row re-renders after scrolling stops
    }),
    [
      filteredChannels,
      programsByChannelId,
      rowHeights,
      renderProgram,
      handleLogoClick,
      contentWidth,
      guideWidth,
      timelineStartMs,
      settledScrollLeft,
    ]
  );

  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0, true);
    }
  }, [rowHeights]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollToItem(0);
    }
  }, [searchQuery, selectedGroupId, selectedProfileId]);

  // Create profile options for dropdown
  const profileOptions = useMemo(() => getProfileOptions(profiles), [profiles]);

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedGroupId('all');
    setSelectedProfileId('all');
  };

  // Handle group selection changes, ensuring null becomes 'all'
  const handleGroupChange = (value) => {
    setSelectedGroupId(value || 'all');
  };

  // Handle profile selection changes, ensuring null becomes 'all'
  const handleProfileChange = (value) => {
    setSelectedProfileId(value || 'all');
  };

  const handleClearSearchQuery = () => {
    setSearchQuery('');
  };
  const handleChangeSearchQuery = (e) => {
    setSearchQuery(e.target.value);
  };

  return (
    <Box
      ref={tvGuideRef}
      className="tv-guide"
      style={{
        overflow: 'hidden',
      }}
      w={'100%'}
      h={'100%'}
      c="#ffffff"
      ff={'Roboto, sans-serif'}
    >
      {/* Sticky top bar */}
      <Flex
        direction="column"
        style={{
          zIndex: 1000,
          position: 'sticky',
        }}
        c="#ffffff"
        p={'12px 20px'}
        top={0}
      >
        {/* Title and current time */}
        <Flex justify="space-between" align="center" mb={12}>
          <Title order={3} fw={'bold'}>
            TV Guide
          </Title>
          <Flex align="center" gap="md">
            <Text>
              {format(now, `dddd, ${dateYearFormat} • ${timeFormat}`)}
            </Text>
            <Tooltip label="Jump to current time">
              <ActionIcon
                onClick={scrollToNow}
                variant="filled"
                size="md"
                radius="xl"
                color="teal"
              >
                <Clock size={16} />
              </ActionIcon>
            </Tooltip>
          </Flex>
        </Flex>

        {/* Filter controls */}
        <Flex gap="md" align="center">
          <TextInput
            placeholder="Search channels..."
            value={searchQuery}
            onChange={handleChangeSearchQuery}
            w={'250px'} // Reduced width from flex: 1
            leftSection={<Search size={16} />}
            rightSection={
              searchQuery ? (
                <ActionIcon
                  onClick={handleClearSearchQuery}
                  variant="subtle"
                  color="gray"
                  size="sm"
                >
                  <X size={14} />
                </ActionIcon>
              ) : null
            }
          />

          <ProviderGroupMenu
            options={groupOptions}
            value={selectedGroupId}
            onChange={handleGroupChange}
            width={220}
            clearable={allowAllGroups}
          />

          <Select
            placeholder="Filter by profile"
            data={profileOptions}
            value={selectedProfileId}
            onChange={handleProfileChange} // Use the new handler
            w={'180px'}
            clearable={true} // Allow clearing the selection
          />

          {(searchQuery !== '' ||
            selectedGroupId !== 'all' ||
            selectedProfileId !== 'all') && (
            <Button variant="subtle" onClick={clearFilters} size="sm">
              Clear Filters
            </Button>
          )}

          {canManage && (
            <Button
              variant="filled"
              size="sm"
              onClick={openRules}
              style={{
                backgroundColor: '#245043',
              }}
              bd={'1px solid #3BA882'}
              color="#FFFFFF"
            >
              Series Rules
            </Button>
          )}

          <Text size="sm" c="dimmed">
            {filteredChannels.length}{' '}
            {filteredChannels.length === 1 ? 'channel' : 'channels'}
          </Text>
        </Flex>
      </Flex>

      {/* Guide container with headers and scrollable content */}
      <Box
        style={{
          flexDirection: 'column',
        }}
        display={'flex'}
        h={'calc(100vh - 120px)'}
      >
        {/* Logo header - Sticky, non-scrollable */}
        <Box
          style={{
            zIndex: 100,
            position: 'sticky',
          }}
          display={'flex'}
          top={0}
        >
          {/* Logo header cell - sticky in both directions */}
          <Box
            style={{
              flexShrink: 0,
              backgroundColor: '#1B2421',
              borderBottom: '1px solid #27272A',
              borderRight: '1px solid #8DAFAA',
              zIndex: 200,
            }}
            w={CHANNEL_WIDTH}
            miw={CHANNEL_WIDTH}
            h={'40px'}
            pos="sticky"
            left={0}
          />

          {/* Timeline header with its own scrollbar */}
          <Box
            style={{
              flex: 1,
              overflow: 'hidden',
            }}
            pos="relative"
          >
            <Box
              ref={timelineRef}
              style={{
                overflowX: 'auto',
                overflowY: 'hidden',
              }}
              pos="relative"
              onScroll={handleTimelineScroll}
              onWheel={handleTimelineWheel} // Add wheel event handler
            >
              <Box
                style={{
                  backgroundColor: '#1E2A27',
                  borderBottom: '1px solid #27272A',
                }}
                display={'flex'}
                w={hourTimeline.length * HOUR_WIDTH}
                pos="relative"
              >
                <HourTimeline
                  hourTimeline={hourTimeline}
                  timeFormat={timeFormat}
                  formatDayLabel={formatDayLabel}
                  handleTimeClick={handleTimeClick}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Main scrollable container for program content */}
        <Box
          ref={guideContainerRef}
          style={{
            flex: 1,
            overflow: 'hidden',
          }}
          pos="relative"
        >
          <LoadingOverlay visible={isProgramsLoading || isChannelsLoading} />
          {nowPosition >= 0 && (
            <Box
              ref={nowLineRef}
              style={{
                backgroundColor: '#38b2ac',
                zIndex: 15,
                pointerEvents: 'none',
                left: `${((Date.now() - timelineStartMs) / 60000 / MINUTE_INCREMENT) * MINUTE_BLOCK_WIDTH + CHANNEL_WIDTH - guideScrollLeftRef.current}px`,
              }}
              pos="absolute"
              top={0}
              bottom={0}
              w={'2px'}
            >
              {/* Now-marker triangle at top of line */}
              <Box
                pos="absolute"
                top={-1}
                style={{
                  transform: 'translateX(-6px)',
                  width: 0,
                  height: 0,
                  borderLeft: '7px solid transparent',
                  borderRight: '7px solid transparent',
                  borderTop: '9px solid #38b2ac',
                  filter: 'drop-shadow(0 1px 3px rgba(56, 178, 172, 0.5))',
                }}
              />
            </Box>
          )}

          {filteredChannels.length > 0 ? (
            <VariableSizeList
              className="guide-list-outer"
              height={virtualizedHeight}
              width={virtualizedWidth}
              itemCount={filteredChannels.length}
              itemSize={getItemSize}
              estimatedItemSize={PROGRAM_HEIGHT}
              itemKey={itemKey}
              itemData={listData}
              ref={listRef}
              outerRef={guideRef}
              overscanCount={3}
            >
              {GuideRow}
            </VariableSizeList>
          ) : (
            <Box p={'30px'} ta="center" color="#a0aec0">
              <Text size="lg">No channels match your filters</Text>
              <Button variant="subtle" onClick={clearFilters} mt={10}>
                Clear Filters
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Record choice modal */}
      {recordChoiceOpen && recordChoiceProgram && (
        <ErrorBoundary inline>
          <Suspense fallback={<LoadingOverlay />}>
            <ProgramRecordingModal
              opened={recordChoiceOpen}
              onClose={() => setRecordChoiceOpen(false)}
              program={recordChoiceProgram}
              recording={recordingForProgram}
              existingRuleMode={existingRuleMode}
              existingRule={existingRule}
              epgSourceId={
                recordChoiceChannel?.epg_data_id
                  ? tvgsById[recordChoiceChannel.epg_data_id]?.epg_source
                  : null
              }
              onRecordOne={() =>
                recordOne(recordChoiceProgram, recordChoiceChannel)
              }
              onRecordSeriesAll={() =>
                saveSeriesRule(recordChoiceProgram, 'all')
              }
              onRecordSeriesNew={() =>
                saveSeriesRule(recordChoiceProgram, 'new')
              }
              onExistingRuleModeChange={setExistingRuleMode}
            />
          </Suspense>
        </ErrorBoundary>
      )}

      {/* Series rules modal */}
      {rulesOpen && (
        <ErrorBoundary inline>
          <Suspense fallback={<LoadingOverlay />}>
            <SeriesRecordingModal
              opened={rulesOpen}
              onClose={() => setRulesOpen(false)}
              rules={rules}
              onRulesUpdate={setRules}
            />
          </Suspense>
        </ErrorBoundary>
      )}

      {/* Program detail modal */}
      {selectedProgram && (
        <ErrorBoundary inline>
          <Suspense fallback={<LoadingOverlay />}>
            <ProgramDetailModal
              program={selectedProgram}
              channel={selectedChannel}
              isGuideLive={selectedProgramIsLive}
              recording={recordingForProgram}
              opened={!!selectedProgram}
              onClose={handleCloseModal}
              onRecord={
                canManage
                  ? (program) => openRecordChoice(program, selectedChannel)
                  : undefined
              }
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </Box>
  );
}
